import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaTimes, FaGripVertical } from 'react-icons/fa';
import { videoStorage } from '../../utils/videoStorage.js';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Video Item Component
const SortableVideoItem = ({ id, index, videoUrl, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group"
    >
      <div className="relative">
        <div className="relative border-2 border-gray-200 hover:border-primary-500 rounded-lg transition-colors overflow-hidden">
          <video
            src={videoUrl}
            className="w-full h-32 object-cover"
            controls
            preload="metadata"
          />
          {/* Position indicator */}
          <div className="absolute top-2 left-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-xs font-semibold">
            Video #{index + 1}
          </div>
          
          {/* Drag handle - this is the draggable area */}
          <div
            {...attributes}
            {...listeners}
            className="absolute bottom-2 left-2 bg-black bg-opacity-60 text-white p-2 rounded cursor-grab active:cursor-grabbing hover:bg-opacity-80 transition-colors"
            title="Drag to reorder"
          >
            <FaGripVertical className="w-4 h-4" />
          </div>
        </div>
        
        {/* Delete button */}
        <button
          onClick={() => onDelete(videoUrl)}
          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-20"
          title="Delete video"
        >
          <FaTimes className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

const VideoUpload = ({ vehicleId, onVideoUploaded, existingVideos = [] }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    // Convert FileList to Array
    const fileArray = Array.from(files);
    
    console.log(`Uploading ${fileArray.length} video(s)`);
    
    // Validate all video files
    for (const file of fileArray) {
      const validation = videoStorage.validateVideo(file);
      if (!validation.valid) {
        alert(`${file.name}: ${validation.error}`);
        return;
      }
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const totalFiles = fileArray.length;
      let completedFiles = 0;
      const uploadedUrls = [];

      // Upload each file
      for (const file of fileArray) {
        // Update progress for current file
        setUploadProgress(Math.floor((completedFiles / totalFiles) * 100));
        
        console.log(`Uploading video ${completedFiles + 1} of ${totalFiles}: ${file.name}`);
        const result = await videoStorage.uploadVideo(file, vehicleId, 'showcase');
        
        if (result.success) {
          console.log(`Successfully uploaded: ${file.name}`);
          uploadedUrls.push(result.url);
          completedFiles++;
        } else {
          throw new Error(`Failed to upload ${file.name}: ${result.error}`);
        }
      }
      
      // Call onVideoUploaded with all URLs at once
      if (uploadedUrls.length > 0) {
        onVideoUploaded(uploadedUrls);
      }
      
      setUploadProgress(100);
      
      // Clear the file input to allow re-selection
      const fileInput = document.getElementById('video-upload');
      if (fileInput) fileInput.value = '';
      
      setTimeout(() => {
        setUploadProgress(0);
        setUploading(false);
      }, 1000);
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed: ' + error.message);
      setUploading(false);
      setUploadProgress(0);
      
      // Clear file input on error too
      const fileInput = document.getElementById('video-upload');
      if (fileInput) fileInput.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    console.log(`Dropped ${files.length} file(s)`);
    handleFileSelect(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDeleteVideo = async (videoUrl) => {
    try {
      const fileName = videoStorage.getFileNameFromUrl(videoUrl);
      if (!fileName) {
        console.error('Could not extract filename from URL:', videoUrl);
        return;
      }

      const confirmed = window.confirm('Are you sure you want to delete this video?');
      if (!confirmed) return;

      const result = await videoStorage.deleteVideo(fileName);
      if (result.success) {
        // Call parent handler to update state
        if (onVideoUploaded) {
          onVideoUploaded(null, videoUrl); // Signal deletion
        }
      } else {
        alert('Failed to delete video: ' + result.error);
      }
    } catch (error) {
      console.error('Error in handleDeleteVideo:', error);
      alert('An error occurred while deleting the video. Please try again.');
    }
  };

  // Setup drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const oldIndex = existingVideos.findIndex((vid) => vid === active.id);
      const newIndex = existingVideos.findIndex((vid) => vid === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newVideos = arrayMove(existingVideos, oldIndex, newIndex);
        if (onVideoUploaded) {
          onVideoUploaded(newVideos, null, true); // third param indicates reorder
        }
      }
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Vehicle Videos</h3>
      
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
          dragActive 
            ? 'border-primary-500 bg-primary-50' 
            : 'border-gray-300 hover:border-gray-400'
        } ${uploading ? 'pointer-events-none opacity-50' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className="mt-4">
            <label htmlFor="video-upload" className="cursor-pointer">
              <span className="mt-2 block text-sm font-medium text-gray-900">
                {uploading ? 'Uploading...' : 'Drop video files here or click to browse (select multiple)'}
              </span>
              <span className="mt-1 block text-xs text-gray-500">
                MP4, WebM, OGG, AVI, MOV up to 300MB each - Hold Ctrl/Cmd to select multiple files
              </span>
            </label>
            <input
              id="video-upload"
              type="file"
              accept="video/*"
              multiple
              onChange={(e) => {
                console.log(`Selected ${e.target.files.length} file(s) via file browser`);
                handleFileSelect(e.target.files);
              }}
              className="hidden"
              disabled={uploading}
            />
          </div>
        </div>

        {/* Upload Progress */}
        {uploading && (
          <div className="mt-4">
            <div className="bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p className="text-sm text-gray-600 mt-1">{uploadProgress}% uploaded</p>
          </div>
        )}
      </div>

      {/* Existing Videos - With Drag and Drop */}
      {existingVideos && existingVideos.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-900">Current Videos</h4>
            <p className="text-sm text-gray-500">Drag videos to reorder</p>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={existingVideos}
              strategy={verticalListSortingStrategy}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {existingVideos.map((videoUrl, index) => (
                  <SortableVideoItem
                    key={videoUrl}
                    id={videoUrl}
                    index={index}
                    videoUrl={videoUrl}
                    onDelete={handleDeleteVideo}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
};

export default VideoUpload;
